import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../provider/AuthProvider';
import { useAppStore } from '../../store/useAppStore';
import { Job } from '../../models/Job';
import { LoadingSpinner } from '../../components/shared/ui/LoadingSpinner';
import SheetGround from '../../components/shared/ui/SheetGround';
import SheetLoading from '../../components/shared/ui/SheetLoading';
import { SubjectMultiSelect, JobTitleCombobox } from '../../components/shared/ui';
import { ROUTE_NAMES } from '../../routes/routeConstants';
import { createJob, updateJob, fetchJobById } from '../../services/jobService';
import {
  VELLUM as T,
  SANS,
  display,
  mono,
  body,
  btn,
  SheetHead,
  TitleBlock,
} from '../../components/shared/folio/kit';
import {
  FP,
  FieldBlock,
  FieldRow,
  fieldInput,
  fieldArea,
  folioFormCss,
} from '../../components/shared/folio/form';
import { FolioSelect } from '../../components/shared/folio/Select';

/* Post a role, drawn as a sheet.

   A job posting is a schedule of particulars, which is what a drawing's
   title block already is - so the form is ruled as one rather than given a
   second, unrelated form language. The measure carries the schedule; the
   margin carries the terms it is filed under, which is where an employer
   looks for the fee and the review before committing to the fields.

   The apparatus is the same one /contact and the auth dialogs use, so
   filling anything in on this site is the same act twice. */

const P = 'pj';

/* The schedules of permitted values. Kept at module scope so they are not
   rebuilt on every keystroke in the form. */
const WORK_TYPES = [
  { value: 'On Site', label: 'On site' },
  { value: 'Remote', label: 'Remote' },
  { value: 'Hybrid', label: 'Hybrid' },
];

const EMPLOYMENT_TYPES = [
  { value: 'Full Time', label: 'Full time' },
  { value: 'Part Time', label: 'Part time' },
  { value: 'Contract', label: 'Contract' },
];

const EXPERIENCE_LEVELS = [
  { value: '1-2 Years', label: '1–2 years' },
  { value: '3-5 Years', label: '3–5 years' },
  { value: '6-9 Years', label: '6–9 years' },
  { value: '10+ Years', label: '10+ years' },
];

const HE_STATUS = ['None', 'Associate Fellow', 'Fellow', 'Senior Fellow'];

const CONTRACT_DURATIONS = [
  { value: 'Permanent', label: 'Permanent' },
  { value: 'Fixed 1 Year', label: 'Fixed — 1 year' },
  { value: 'Fixed 2 Years', label: 'Fixed — 2 years' },
  { value: 'Fixed 3 Years', label: 'Fixed — 3 years' },
  { value: 'Fixed 4 Years', label: 'Fixed — 4 years' },
];

const CURRENCIES = [
  { value: 'GBP', label: '£ GBP' },
  { value: 'EUR', label: '€ EUR' },
];

/* One particular of the listing, ruled in the margin. */
const NOTES: [string, string][] = [
  ['Fee', '£89 per listing'],
  ['Review', 'By an editor'],
  ['Live', 'Within 24 hours'],
  ['Window', '30 days, editable'],
];

const CHECKLIST = [
  'Name the department, not just the institution — scholars filter on it.',
  'Give a real salary range. Listings without one are read half as often.',
  'One responsibility per line. The register renders them as a schedule.',
  'Tag every subject the post touches, not only the primary one.',
];

const PostJobPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editJobId } = useParams<{ id: string }>();
  const isEditMode = !!editJobId;
  const addToast = useAppStore((state) => state.addToast);
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(isEditMode);
  const [formData, setFormData] = useState<Partial<Job>>({
    title: '',
    location: '',
    type: 'On Site',
    category: '',
    employmentType: 'Full Time',
    experience: '3-5 Years',
    careerLevel: 'None',
    contractDuration: 'Permanent',
    subjects: [],
    startDate: '',
    salary: '',
    salaryValue: 0,
    logoUrl: '',
    description: '',
    daysLeft: 30,
    hot: false,
  });
  const [currency, setCurrency] = useState('GBP');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [responsibilitiesText, setResponsibilitiesText] = useState('');
  const [requirementsText, setRequirementsText] = useState('');

  // Edit mode: fetch the existing job and pre-populate the form.
  useEffect(() => {
    if (!editJobId) return;
    let cancelled = false;
    (async () => {
      try {
        const job = await fetchJobById(editJobId);
        if (cancelled) return;
        if (!job) {
          addToast({ type: 'error', title: 'Not found', message: 'Listing does not exist.', duration: 4000 });
          navigate(ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD);
          return;
        }
        setFormData({
          title: job.title || '',
          location: job.location || '',
          type: job.type || 'On Site',
          category: job.category || '',
          jobCategory: job.jobCategory,
          employmentType: job.employmentType || 'Full Time',
          experience: job.experience || '3-5 Years',
          careerLevel: job.careerLevel || 'None',
          contractDuration: job.contractDuration || 'Permanent',
          subjects: job.subjects || [],
          startDate: job.startDate || '',
          salary: job.salary || '',
          salaryValue: job.salaryValue || 0,
          logoUrl: job.logoUrl || '',
          description: job.description || '',
          daysLeft: job.daysLeft ?? 30,
          hot: job.hot ?? false,
          company: job.company,
        });
        // Parse salary string "£40,000 - £60,000" or "£40,000" back into fields.
        const salaryStr = job.salary || '';
        if (salaryStr.startsWith('€')) setCurrency('EUR');
        const nums = salaryStr.match(/[\d,]+/g) || [];
        if (nums[0]) setSalaryMin(nums[0].replace(/,/g, ''));
        if (nums[1]) setSalaryMax(nums[1].replace(/,/g, ''));
        setResponsibilitiesText((job.responsibilities || []).join('\n'));
        setRequirementsText((job.requirements || []).join('\n'));
      } catch (err) {
        if (cancelled) return;
        addToast({ type: 'error', title: 'Failed to load', message: 'Could not load the listing.', duration: 4000 });
        navigate(ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editJobId]);

  const currencySymbols: Record<string, string> = {
    GBP: '£',
    EUR: '€',
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  /* The dropdowns hand back a value rather than an event: FolioSelect is not
     a native control, so there is no e.target.name to key off. */
  const setField = (key: keyof Job, value: string) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build salary string and value
      const symbol = currencySymbols[currency];
      const minVal = parseInt(salaryMin) || 0;
      const maxVal = parseInt(salaryMax) || minVal;
      const salaryString = maxVal > minVal
        ? `${symbol}${minVal.toLocaleString()} - ${symbol}${maxVal.toLocaleString()}`
        : `${symbol}${minVal.toLocaleString()}`;
      const salaryValue = maxVal > 0 ? maxVal : minVal;

      // Parse responsibilities and requirements from text (one per line)
      const responsibilities = responsibilitiesText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      const requirements = requirementsText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      // Prepare job data
      const jobData: Partial<Job> = {
        ...formData,
        salary: salaryString,
        salaryValue: salaryValue,
        responsibilities,
        requirements,
        // Auto-fill company from employer's uniName if not provided
        company: formData.company || (user as any)?.uniName || '',
      };

      if (isEditMode && editJobId) {
        await updateJob(editJobId, jobData);
        addToast({
          type: 'success',
          title: 'Listing updated',
          message: 'Your changes have been saved.',
          duration: 4000
        });
      } else {
        await createJob(jobData);
        addToast({
          type: 'success',
          title: 'Job Posted',
          message: 'Your job has been posted successfully!',
          duration: 4000
        });
      }

      // Redirect to employer dashboard
      navigate(ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD);
    } catch (error) {
      console.error(isEditMode ? 'Failed to update job' : 'Failed to post job');
      addToast({
        type: 'error',
        title: isEditMode ? 'Failed to save' : 'Failed to Post',
        message: error instanceof Error ? error.message : (isEditMode ? 'Failed to save changes. Please try again.' : 'Failed to post job. Please try again.'),
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  /* Refused, on the sheet. A page that turns a visitor away is still the
     same sheet of paper, so it is ruled like one rather than dropping to a
     bare panel in no design system at all. */
  if (!user || user.role !== 'employer') {
    return (
      <SheetGround style={{ minHeight: '100vh', fontFamily: SANS, color: T.ink }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '96px 20px' }}>
          <div style={{ ...mono(T, T.accent), marginBottom: 16 }}>§ Access denied</div>
          <h1 style={{ ...display(T, 'clamp(32px, 4.6vw, 52px)'), maxWidth: '18ch' }}>
            This sheet is for{' '}
            <span style={{ color: T.accent, borderBottom: `3px solid ${T.accent}` }}>institutions</span>.
          </h1>
          <p style={{ ...body(T), fontSize: 17, marginTop: 24, maxWidth: '52ch' }}>
            Posting to the register requires an institutional account. If you hold one, sign in with it
            and this sheet will open.
          </p>
          <button
            type="button"
            onClick={() => navigate(ROUTE_NAMES.PAGES.JOBS)}
            className={`${FP}-btn`}
            style={{ ...btn(T, 'outline'), marginTop: 32 }}
          >
            Browse the register
          </button>
        </div>
      </SheetGround>
    );
  }

  if (hydrating) return <SheetLoading text="Loading listing…" />;

  return (
    <SheetGround className={`${P}-root`} style={{ fontFamily: SANS, color: T.ink }}>
      <div
        className={`${P}-grid`}
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 20px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 1px 296px',
          columnGap: 40,
          alignItems: 'start',
        }}
      >
        <div
          className={`${P}-spine`}
          aria-hidden
          style={{ gridColumn: 2, gridRow: '1 / -1', alignSelf: 'stretch', width: 1, background: T.rule }}
        />

        <section className={`${P}-measure`} style={{ gridColumn: 1, paddingTop: 44, paddingBottom: 80 }}>
          <div
            style={{
              ...mono(T),
              borderBottom: `1px solid ${T.rule}`,
              paddingBottom: 12,
              marginBottom: 30,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <span>{isEditMode ? '§ Edit a listing' : '§ Post a role'}</span>
            <span>Read by an editor</span>
            <span>{isEditMode ? 'Changes go live at once' : '£89 per listing'}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <h1 style={{ ...display(T, 'clamp(36px, 5.4vw, 64px)'), lineHeight: 0.98, letterSpacing: '-0.04em' }}>
              {isEditMode ? (
                <>
                  Edit your{' '}
                  <span style={{ color: T.accent, borderBottom: `3px solid ${T.accent}` }}>listing</span>.
                </>
              ) : (
                <>
                  Post a role to{' '}
                  <span style={{ color: T.accent, borderBottom: `3px solid ${T.accent}` }}>the register</span>.
                </>
              )}
            </h1>
            <span style={mono(T, T.accent)}>{isEditMode ? '§ Amend' : '§ New entry'}</span>
          </div>

          <p style={{ ...body(T), fontSize: 18, marginTop: 26, maxWidth: 620 }}>
            {isEditMode
              ? 'Update the particulars below and save. Your changes appear on the register immediately.'
              : "Fill in the particulars below. An editor reads every listing before it goes live on the register."}
          </p>

          <form onSubmit={handleSubmit} style={{ marginTop: 52 }}>
            {/* § 01 · Role basics ──────────────────────────────────── */}
            <SheetHead t={T} code="§ 01 · Role basics" size="clamp(24px, 3.2vw, 32px)">
              The essentials.
            </SheetHead>

            <FieldBlock>
              <FieldRow label="Title" htmlFor="title" required first stacked>
                <div style={{ padding: '4px 14px 14px' }}>
                  <JobTitleCombobox
                    bare
                    id="title"
                    name="title"
                    value={formData.title || ''}
                    onChange={(next) => setFormData((prev) => ({ ...prev, title: next }))}
                    placeholder="Search or type a job title…"
                    required
                  />
                </div>
              </FieldRow>
              <FieldRow label="Location" htmlFor="location" required>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Cambridge, UK"
                  style={fieldInput}
                  required
                />
              </FieldRow>
              <FieldRow label="Work type" htmlFor="type" required>
                <FolioSelect
                  bare
                  id="type"
                  name="type"
                  required
                  value={formData.type || ''}
                  onChange={(v) => setField('type', v)}
                  options={WORK_TYPES}
                />
              </FieldRow>
            </FieldBlock>

            {/* § 02 · Classification ───────────────────────────────── */}
            <div style={{ marginTop: 52 }}>
              <SheetHead t={T} code="§ 02 · Classification" size="clamp(24px, 3.2vw, 32px)">
                How we file it.
              </SheetHead>
            </div>

            <FieldBlock>
              <FieldRow label="Subjects" htmlFor="subjects" first stacked>
                <div style={{ padding: '4px 14px 14px' }}>
                  <SubjectMultiSelect
                    bare
                    id="subjects"
                    name="subjects"
                    value={formData.subjects || []}
                    onChange={(next) => setFormData((prev) => ({ ...prev, subjects: next }))}
                    placeholder="Search and add academic subjects…"
                  />
                </div>
              </FieldRow>
              <FieldRow label="Employment" htmlFor="employmentType" required>
                <FolioSelect
                  bare
                  id="employmentType"
                  name="employmentType"
                  required
                  value={formData.employmentType || ''}
                  onChange={(v) => setField('employmentType', v)}
                  options={EMPLOYMENT_TYPES}
                />
              </FieldRow>
              <FieldRow label="Experience" htmlFor="experience" required>
                <FolioSelect
                  bare
                  id="experience"
                  name="experience"
                  required
                  value={formData.experience || ''}
                  onChange={(v) => setField('experience', v)}
                  options={EXPERIENCE_LEVELS}
                />
              </FieldRow>
              <FieldRow label="HE status" htmlFor="careerLevel" required>
                <FolioSelect
                  bare
                  id="careerLevel"
                  name="careerLevel"
                  required
                  value={formData.careerLevel || ''}
                  onChange={(v) => setField('careerLevel', v)}
                  options={HE_STATUS}
                />
              </FieldRow>
              <FieldRow label="Duration" htmlFor="contractDuration" required>
                <FolioSelect
                  bare
                  id="contractDuration"
                  name="contractDuration"
                  required
                  value={formData.contractDuration || ''}
                  onChange={(v) => setField('contractDuration', v)}
                  options={CONTRACT_DURATIONS}
                />
              </FieldRow>
              <FieldRow label="Start" htmlFor="startDate">
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate || ''}
                  onChange={handleChange}
                  style={fieldInput}
                />
              </FieldRow>
              {/* Three controls, one figure. They are ruled into a single row
                  because a salary is read as one value, not three. */}
              <FieldRow label="Salary" htmlFor="salaryMin" required>
                <div className={`${P}-salary`} style={{ display: 'flex', alignItems: 'stretch', width: '100%', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <FolioSelect
                      bare
                      id="currency"
                      ariaLabel="Currency"
                      value={currency}
                      onChange={setCurrency}
                      options={CURRENCIES}
                      width={104}
                    />
                  </div>
                  <span aria-hidden style={{ width: 1, background: T.rule, flexShrink: 0 }} />
                  <input
                    type="number"
                    id="salaryMin"
                    value={salaryMin}
                    onChange={(e) => setSalaryMin(e.target.value)}
                    placeholder="40000"
                    aria-label="Minimum salary"
                    style={fieldInput}
                    required
                    min="0"
                  />
                  <span style={{ ...mono(T), display: 'flex', alignItems: 'center', flexShrink: 0 }}>to</span>
                  <input
                    type="number"
                    id="salaryMax"
                    value={salaryMax}
                    onChange={(e) => setSalaryMax(e.target.value)}
                    placeholder="60000"
                    aria-label="Maximum salary — leave empty for a single figure"
                    style={fieldInput}
                    min="0"
                  />
                </div>
              </FieldRow>
            </FieldBlock>

            <p style={{ ...mono(T), marginTop: 10 }}>
              Annual figure. Leave the upper field empty for a single value.
            </p>

            {/* § 03 · Description ──────────────────────────────────── */}
            <div style={{ marginTop: 52 }}>
              <SheetHead t={T} code="§ 03 · Description" size="clamp(24px, 3.2vw, 32px)">
                Tell the story.
              </SheetHead>
            </div>

            <FieldBlock>
              <FieldRow label="Overview" htmlFor="description" required first stacked>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={6}
                  placeholder="A brief overview of the role, the department, and who it reports to."
                  style={fieldArea}
                  required
                />
              </FieldRow>
              <FieldRow label="Responsibilities — one per line" htmlFor="responsibilities" required stacked>
                <textarea
                  id="responsibilities"
                  value={responsibilitiesText}
                  onChange={(e) => setResponsibilitiesText(e.target.value)}
                  rows={5}
                  placeholder={'Lead research projects\nSupervise postgraduate students\nDeliver lectures and seminars'}
                  style={fieldArea}
                  required
                />
              </FieldRow>
              <FieldRow label="Requirements — one per line" htmlFor="requirements" required stacked>
                <textarea
                  id="requirements"
                  value={requirementsText}
                  onChange={(e) => setRequirementsText(e.target.value)}
                  rows={5}
                  placeholder={'PhD in a relevant field\n5+ years teaching experience\nStrong publication record'}
                  style={fieldArea}
                  required
                />
              </FieldRow>
            </FieldBlock>

            {/* § 04 · Listing settings ─────────────────────────────── */}
            <div style={{ marginTop: 52 }}>
              <SheetHead t={T} code="§ 04 · Listing" size="clamp(24px, 3.2vw, 32px)">
                Visibility & timing.
              </SheetHead>
            </div>

            <FieldBlock>
              <FieldRow label="Window" htmlFor="daysLeft" first>
                <input
                  type="number"
                  id="daysLeft"
                  name="daysLeft"
                  value={formData.daysLeft}
                  onChange={handleChange}
                  min="1"
                  max="365"
                  style={fieldInput}
                />
                <span style={{ ...mono(T), paddingRight: 14, flexShrink: 0 }}>days</span>
              </FieldRow>
              <FieldRow label="Logo" htmlFor="logoUrl">
                <input
                  type="url"
                  id="logoUrl"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={handleChange}
                  placeholder="https://example.ac.uk/logo.png"
                  style={fieldInput}
                />
              </FieldRow>
            </FieldBlock>

            {/* ── Actions ────────────────────────────────────────────
                The commitment sits on the left where the reading ends,
                and the way out is quiet beside it rather than given the
                same weight as the act it undoes. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 26, marginTop: 34, flexWrap: 'wrap' }}>
              <button
                type="submit"
                disabled={loading}
                className={`${FP}-btn`}
                style={{ ...btn(T, 'accent'), gap: 12 }}
              >
                {loading && <LoadingSpinner size="sm" color={T.onAccent} />}
                {loading
                  ? (isEditMode ? 'Saving…' : 'Posting…')
                  : (isEditMode ? 'Save changes' : 'Post the role')}
              </button>
              <button
                type="button"
                onClick={() => navigate(ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD)}
                className={`${FP}-quiet`}
                style={{
                  ...mono(T),
                  background: 'none',
                  border: 'none',
                  borderBottom: `1px solid ${T.rule}`,
                  padding: '0 0 3px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>

        <aside className={`${P}-margin`} style={{ gridColumn: 3, paddingTop: 44, paddingBottom: 80 }}>
          <div className={`${P}-margin-inner`}>
            <div style={{ ...mono(T, T.accent), marginBottom: 14 }}>§ Terms</div>
            <h2 style={{ ...display(T, '24px'), borderBottom: `1.5px solid ${T.ink}`, paddingBottom: 11, marginBottom: 18 }}>
              The particulars.
            </h2>
            <TitleBlock t={T} rows={NOTES} />

            <div style={{ ...mono(T, T.accent), margin: '34px 0 14px' }}>§ Before you post</div>
            <h2 style={{ ...display(T, '24px'), borderBottom: `1.5px solid ${T.ink}`, paddingBottom: 11 }}>
              Read well.
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {CHECKLIST.map((c, i) => (
                <li
                  key={c}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0,1fr)',
                    gap: 11,
                    borderBottom: `1px solid ${T.rule}`,
                    padding: '13px 0',
                  }}
                >
                  <span style={{ ...mono(T, T.accent), paddingTop: 2 }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span style={{ ...body(T), fontSize: 13.5, lineHeight: 1.55 }}>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <style>{`
        ${folioFormCss}

        @media (max-width: 980px) {
          .${P}-grid { grid-template-columns: 1px minmax(0, 1fr) !important; column-gap: 0 !important; }
          .${P}-spine { grid-column: 1 !important; }
          .${P}-measure, .${P}-margin { grid-column: 2 !important; padding-left: 22px !important; }
          .${P}-margin { padding-top: 0 !important; }
          .${P}-margin-inner { border-top: 1px solid ${T.rule}; padding-top: 22px; }
        }

        @media (max-width: 560px) {
          /* Currency, floor and ceiling stop fitting on one line; the figure
             takes two, with the rule between them redrawn horizontally. */
          .${P}-salary { flex-wrap: wrap; }
          .${P}-salary > span[aria-hidden] { width: 100% !important; height: 1px; }
        }
      `}</style>
    </SheetGround>
  );
};

export default PostJobPage;
